import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import isEmpty from 'lodash/isEmpty';
import { z } from 'zod';
import { SnippetFieldsInputIds } from '../../types';
import { useSnippetById } from '../../hooks/useSnippetById';
import { useTranslations } from '../../hooks/useTranslations';

type Props = {
  snippetId: number;
};

export const SnippetEditorFormProvider: FC<React.PropsWithChildren<Props>> = ({
  children,
  snippetId,
}) => {
  const { t } = useTranslations();

  const schema = z.object({
    [SnippetFieldsInputIds.content]: z.string(),
    [SnippetFieldsInputIds.description]: z.string().optional(),
    [SnippetFieldsInputIds.name]: z
      .string()
      .min(1, { message: t('error.missingSnippetName') })
      .regex(new RegExp(/^[\w\-\s]+$/), { message: t('error.invalidSnippetName') })
      .max(100, { message: t('error.invalidSnippetName') }),
    [SnippetFieldsInputIds.positionalParameters]: z.array(z.string()),
  });

  type SnippetFormSchema = Required<z.infer<typeof schema>>;

  const { isLoading = true, results: snippet } = useSnippetById({ id: snippetId });

  const methods = useForm<SnippetFormSchema>({
    defaultValues: {
      [SnippetFieldsInputIds.content]: snippet.content,
      [SnippetFieldsInputIds.description]: snippet.description,
      [SnippetFieldsInputIds.name]: snippet.name,
      [SnippetFieldsInputIds.positionalParameters]: snippet.positionalParameters,
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!isLoading && !isEmpty(snippet)) {
      methods.reset({
        [SnippetFieldsInputIds.content]: snippet.content,
        [SnippetFieldsInputIds.description]: snippet.description,
        [SnippetFieldsInputIds.name]: snippet.name,
        [SnippetFieldsInputIds.positionalParameters]: snippet.positionalParameters,
      });
    }
  }, [isLoading, snippet]);

  return <FormProvider {...methods}>{children}</FormProvider>;
};
